import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  BasicCreatureInstance,
  CustomParticipant,
  Encounter, 
  // EncounterLog, 
  EncounterParticipant,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import InitiativeTrackerParticipant from "@dndComponents/encounter/InitiativeTrackerParticipant";
import CheckBox from "@components/input/CheckBox";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';
import FlexBox from '@components/layout/FlexBox';

interface Props { 
  obj: Encounter;
  change_time: number;
  onChange: (recalc: boolean) => void;
  onSelect: (selected_participant_id: string, selected_ci_id?: string) => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function InitiativeTracker({
  obj,
  change_time,
  onChange,
  onSelect,
}: Props) {
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [reloading, setReloading] = useState(false);
  const [selected_participant_id, setSelectedParticipantId] = useState("");
  const [selected_ci_id, setSelectedCIId] = useState("");

  useEffect(() => {
    if (change_time !== prev_change_time) {
      setChangeTime(change_time);
    }
  }, [change_time, prev_change_time, setChangeTime]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const updateEncounter = (onFinished: Function | null = null) => {
    onChange(true);
    if (onFinished) {
      onFinished();
    }
  } 

  const nextTurn = (participants: EncounterParticipant[], onFinished: Function | null = null) => {
    let current_turn_participant = participants.find(o => o.participant_id === obj.current_turn_participant_id);
    if (current_turn_participant && current_turn_participant.initiative) {
      const initiative = current_turn_participant.initiative;
      const add_time = current_turn_participant.add_time;
      // debugger;
      const later_participants = participants.filter(o => o.initiative !== null && (o.initiative < initiative || (o.initiative === initiative && o.add_time > add_time))).sort((a, b) => { 
        if (a.initiative === null || b.initiative === null) {
          return -1;
        } else if (a.initiative === b.initiative) {
          return +a.add_time > +b.add_time ? 1 : -1;
        } else {
          return +a.initiative < +b.initiative ? 1 : -1;
        }
      });
      if (later_participants.length > 0) {
        obj.current_turn_participant_id = later_participants[0].participant_id;
      } else {
        obj.current_turn_participant_id = participants[0].participant_id;
      }
      // const logs: EncounterLog[] = [];
      // const log = new EncounterLog();
      // log.field = "current_turn_participant_id";
      // log.value = obj.current_turn_participant_id;
      // logs.push(log);
      current_turn_participant = participants.filter(o => o.participant_id === obj.current_turn_participant_id)[0];
      updateEncounter(onFinished);
    } else {
      current_turn_participant = participants.find(o => 
        o.initiative !== null
      );
    }
  }
  
  const owner = api.user_id === obj.owner_id || (obj.campaign && obj.campaign.owner_id === api.user_id);
  let participants = owner ? obj.participants : obj.participants.filter(o => !o.hide);
  if (obj.running) {
    participants = participants.sort((a, b) => { 
      if (a.initiative === null || b.initiative === null) {
        if (a.initiative !== null) {
          return -1;
        } else if (b.initiative !== null) {
          return 1;
        } else {
          if (a.include_in_difficulty && !b.include_in_difficulty) {
            return -1;
          } else if (!a.include_in_difficulty && b.include_in_difficulty) {
            return 1;
          } else {
            return 0;
          }
        }
      } else if (a.initiative === b.initiative) {
        return +a.add_time > +b.add_time ? 1 : -1;
      } else {
        return +a.initiative < +b.initiative ? 1 : -1;
      }
    });
  }
  return (
    <FlexBox direction="column" style={{ height: "100%" }}>
      { owner &&
        <FlexBox xs_expand={0} xs_shrink={0} direction="column">
          <FlexBox xs_expand={1} xs_shrink={1} direction="row">
            <FlexBox>
              <DandDButtonBox 
                name={ obj.running ? "Pause" : "Start" }
                disabled={ participants.filter(o => o.initiative !== null).length === 0 }
                onClick={() => {
                  const new_obj = obj;
                  new_obj.running = !new_obj.running;
                  if (new_obj.running) {
                    if (new_obj.current_turn_participant_id === "" && participants.filter(o => o.initiative !== null).length > 0) {
                      new_obj.current_turn_participant_id = participants.filter(o => o.initiative !== null)[0].participant_id;
                    }
                  }
                  updateEncounter();
                  setReloading(true);
                }}
              />
            </FlexBox>
            <FlexBox>
              <DandDButtonBox 
                name="End"
                disabled={ !obj.running }
                onClick={() => {
                  const new_obj = obj;
                  new_obj.running = false;
                  new_obj.current_turn_participant_id = "";
                  new_obj.current_initiative = -10;
                  updateEncounter();
                  setReloading(true);
                }}
              />
            </FlexBox>
            <FlexBox>
              <DandDButtonBox 
                name="Next Turn"
                disabled={ !obj.running }
                onClick={() => {
                  nextTurn(participants);
                }}
              />
            </FlexBox>
          </FlexBox>
          <FlexBox xs_expand={1} xs_shrink={1} direction="row">
            <FlexBox>
              <DandDButtonBox 
                name="Roll Missing"
                disabled={ obj.running || obj.participants.filter(o => o.initiative === null && o.include_in_difficulty).length === 0 }
                onClick={() => {
                  const new_obj = obj;
                  // debugger;
                  for (const participant of new_obj.participants.filter(o => 
                    o.initiative === null && o.include_in_difficulty)) {
                    const adv = participant.initiative_adv;
                    let roll = common_util.get_random(20);
                    if (adv > 0) {
                      roll = Math.max(roll, common_util.get_random(20));
                    } else if (adv < 0) {
                      roll = Math.min(roll, common_util.get_random(20));
                    }
                    // console.log(roll);
                    participant.initiative = Math.max(0, roll + participant.initiative_modifier);
                  }
                  updateEncounter();
                }}
              />
            </FlexBox>
            <FlexBox>
              <DandDButtonBox 
                name="Use Passives"
                disabled={ obj.running || obj.participants.filter(o => o.initiative === null && o.include_in_difficulty).length === 0 }
                onClick={() => {
                  const new_obj = obj;
                  for (const participant of new_obj.participants.filter(o => 
                    o.initiative === null && o.include_in_difficulty)) {
                    participant.initiative = 10 + participant.initiative_modifier;
                  }
                  updateEncounter();
                }}
              />
            </FlexBox>
          </FlexBox>
          <FlexBox xs_expand={1} xs_shrink={1} direction="row">
            <FlexBox>
              <DandDButtonBox 
                name="Alternating Initiatives"
                disabled={ obj.running || common_util.unique_array(obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions").map(o => o.color)).length < 2 }
                onClick={() => {
                  debugger; 
                  // This is supposed to group them by team, keeping init order, 
                  // then re-sort them so it alternates between teams, back and forth.
                  // Currently it's just causing it to freeze however.
                  const the_participants = obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions");
                  const colors: string[] = [];
                  const highest_inits: number[] = [];
                  const teams: { [color: string]: { pos: number, participants: EncounterParticipant[] } } = {};

                  for (const part of the_participants) {
                    const color = part.color;
                    if (teams[color] === undefined) {
                      teams[color] = { pos: colors.length, participants: [] };
                      colors.push(color);
                      highest_inits.push(-5);
                    }
                    teams[color].participants.push(part);
                    if (part.initiative !== null) {
                      highest_inits[teams[color].pos] = Math.max(highest_inits[teams[color].pos], part.initiative);
                    }
                  }
                  const colors_in_order: string[] = [];
                  for (const color of colors) {

                  }
                  updateEncounter();
                }}
              />
            </FlexBox>
            <FlexBox>
              <DandDButtonBox 
                name="Clear Initiatives"
                disabled={ obj.running || obj.current_turn_participant_id !== "" || obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions").length === 0 }
                onClick={() => {
                  const new_obj = obj;
                  const the_participants = new_obj.participants.filter(o => o.initiative !== null && o.participant_type !== "Lair Actions");
                  for (const part of the_participants) {
                    part.initiative = null;
                  }
                  // const log = new EncounterLog();
                  // log.field = "participants";
                  // log.value = "refresh";
                  updateEncounter();
                }}
              />
            </FlexBox>
          </FlexBox>
        </FlexBox>
      }
      <FlexBox className="padded bordered">
        <FlexBox direction="column" style={{ height: "100%", overflowY: "scroll" }}>
          <Grid container spacing={0} direction="row">
            <Grid size={12}>
              <Grid container spacing={0} direction="row">
                { !reloading && participants.map(participant => {
                  return (
                    <Grid size={12} key={participant.participant_id}>
                      <InitiativeTrackerParticipant
                        obj={participant}
                        encounter={obj}
                        change_time={change_time}
                        selected_participant_id={selected_participant_id}
                        selected_ci_id={selected_ci_id}
                        current_turn_participant_id={obj.current_turn_participant_id}
                        onSelect={(ci: BasicCreatureInstance | CustomParticipant | null) => {
                          if (ci) {
                            const new_id = (participant.participant_id === selected_participant_id && ci.true_id === selected_ci_id ? "" : participant.participant_id);
                            setSelectedParticipantId(new_id);
                            setSelectedCIId(new_id === "" ? "" : ci.true_id);
                            onSelect(new_id, new_id === "" ? "" : ci.true_id);
                          } else {
                            const new_id = (participant.participant_id === selected_participant_id ? "" : participant.participant_id);
                            setSelectedParticipantId(new_id);
                            onSelect(new_id);
                          }
                        }}
                        onChange={() => {
                          updateEncounter();
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
}
